import md5 from 'md5';
import { parse } from 'qs';
import Store from 'repatch';

import State from '../core/State';
import YahooAdAccount from '../entity/YahooAdAccount';
import ErrorPresenter from '../service/ErrorPresenter';
import MessageDispatcher from '../service/MessageDispatcher';
import YahooAdWebApi from '../service/persistent/YahooAdWebApi';
import { setUploadedAdPlatformKeys } from '../thunk/creativeSending';
import {
  decrement as decrementFullScreenSpinnerNecessity,
  increment as incrementFullScreenSpinnerNecessity,
  setLoadingMessage,
} from '../thunk/fullScreenSpinner';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';
import {
  finishIntegratedYahooCommunication,
  setIntegratedYahooAdAccounts,
  setYahooAdAccounts,
  setYahooAdAccountTokenId,
  setYahooUploadedCreative,
  startIntegratedYahooAdAccounts,
} from '../thunk/yahooAdAccount';
import { OrganizationDetailFragment } from './../generated/graphql';

class IntegrateYahooAdAccountUsecase {
  private store: Store<State>;
  private errorPresenter: ErrorPresenter;
  private messageDispatcher: MessageDispatcher;
  private yahooAdWebApi: YahooAdWebApi;

  constructor({
    store,
    errorPresenter,
    yahooAdWebApi,
    messageDispatcher,
  }: {
    store: Store<State>;
    errorPresenter: ErrorPresenter;
    yahooAdWebApi: YahooAdWebApi;
    messageDispatcher: MessageDispatcher;
  }) {
    this.store = store;
    this.errorPresenter = errorPresenter;
    this.yahooAdWebApi = yahooAdWebApi;
    this.messageDispatcher = messageDispatcher;
  }

  public fetchAd = () => {
    const state: State = this.store.getState();
    const query = parse(state.location.search.slice(1));
    const organizationId: string | null = query.org || null;
    const csrf = md5(new Date().getTime().toString());

    window.localStorage.setItem('yahoo-collector-csrf', csrf);
    window.localStorage.setItem('yahoo-collector-target-orgId', organizationId || '');
    window.location.href = `https://biz-oauth.yahoo.co.jp/oauth/v1/authorize?response_type=code&client_id=16a62137dddeac9765588e88f6e908a78b43ca6ce8bec5aa7d4749be054a8ae9&redirect_uri=${window.location.origin}&scope=yahooads&state=${csrf}`;
  };

  /**
   * YahooAdAccount連携のログイン後、リダイレクトによってページ遷移してきた時のみ呼ばれる
   */
  public fetchAccessibleYahooAdAccounts = async (): Promise<boolean | void> => {
    if (location.href.match(/\?yahooAdAuthCode=/)) {
      const query = parse(location.search.substr(1));
      const authCode = query.yahooAdAuthCode;
      if (authCode) {
        this.store.dispatch(incrementFullScreenSpinnerNecessity());
        try {
          const tokenId = await this.yahooAdWebApi.registerYahooAccount(authCode, query.org);
          this.store.dispatch(setYahooAdAccountTokenId(tokenId));
          // 連携可能なアカウント一覧
          const accessibleYahooAdAccounts = await this.yahooAdWebApi.getAllAccessibleYahooAdAccounts(
            this.getCurrentOrganization().id,
            tokenId,
          );

          this.store.dispatch(setYahooAdAccounts(accessibleYahooAdAccounts));
          if (accessibleYahooAdAccounts.size === 0) {
            this.messageDispatcher.notifyUnimportant('message.yahoo_no_linkable_ad_account');
          }
          return true;
        } catch (err) {
          this.errorPresenter.showAsNonFatal(err);
          throw err;
        } finally {
          this.store.dispatch(decrementFullScreenSpinnerNecessity());
        }
      }
    }
  };

  public integrateYahooAdAccount = async (yahooAdAccount: YahooAdAccount): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    try {
      const state: State = this.store.getState();
      const tokenId = state.integratedYahooAdAccounts.tokenId;
      this.store.dispatch(setLoadingMessage('message.yahoo_now_integrating'));
      const isSuccess = await this.yahooAdWebApi.integrateAdAccount(
        yahooAdAccount.adAccountId,
        tokenId,
        this.getCurrentOrganization().id,
      );
      if (isSuccess) {
        this.messageDispatcher.notifyUnimportant('message.yahoo_integrated');
      } else {
        this.errorPresenter.showAsNonFatal(new Error('message.yahoo_integrated_failed'));
      }
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
    this.fetchIntegratedYahooAdAccounts();
  };

  public fetchIntegratedYahooAdAccounts = async (orgId?: string): Promise<boolean | void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    this.store.dispatch(startIntegratedYahooAdAccounts());

    try {
      const integratedYahooAdAccounts = await this.yahooAdWebApi.getAllIntegratedAdAccounts(
        orgId || this.getCurrentOrganization().id,
      );
      this.store.dispatch(setIntegratedYahooAdAccounts(integratedYahooAdAccounts));
      return true;
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public fetchUploadedYahooCreatives = async (creativeId: string, orgId?: string): Promise<boolean | void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    try {
      const uploadedYahooCreatives = await this.yahooAdWebApi.getUploadedYahooCreatives(
        orgId || this.getCurrentOrganization().id,
        [creativeId],
      );
      this.store.dispatch(setYahooUploadedCreative(!!uploadedYahooCreatives.length));
      return !!uploadedYahooCreatives.length;
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public unlinkYahooAdAccount = async (yahooAdAccount: YahooAdAccount): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    this.store.dispatch(startIntegratedYahooAdAccounts());
    try {
      const isSuccess = await this.yahooAdWebApi.unlinkYahooAdAccount(
        this.getCurrentOrganization().id,
        yahooAdAccount.adAccountId,
      );
      if (isSuccess) {
        this.messageDispatcher.notifyUnimportant('message.yahoo_revoked');
      } else {
        this.errorPresenter.showAsNonFatal(new Error('message.yahoo_revoked_failed'));
      }
      this.store.dispatch(finishIntegratedYahooCommunication());
    } catch (err) {
      this.errorPresenter.showAsFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
    this.fetchIntegratedYahooAdAccounts();
  };

  // 連携yahooアカウントにcreativeを送信する
  public sendCreative = async (
    deliverable: {
      id: string;
      creative: {
        id: string;
      };
    },
    yahooAdAccount: YahooAdAccount,
    projectId: string | null,
  ): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    if (projectId) {
      try {
        this.store.dispatch(setLoadingMessage('message.creative_now_sending'));

        await this.yahooAdWebApi.sendCreativeToYahoo(
          deliverable.creative.id,
          this.getCurrentOrganization().id,
          projectId,
          yahooAdAccount.adAccountId,
        );

        this.messageDispatcher.notifyUnimportant('message.creative_has_sent_to_yahoo_ad_asset_library', {
          action: 'message.creative_has_sent_to_yahoo_ad_asset_library.action',
          onClickAction: () => this.transitionToYahooAdAssetLibrary(yahooAdAccount),
        });

        const uploadedAdPlatformKeys =
          this.store.getState().creativeSending.uploadedAdPlatformKeys.get(deliverable.id) || [];
        this.store.dispatch(setUploadedAdPlatformKeys(deliverable.id, [...uploadedAdPlatformKeys, 'yahoo']));
      } catch (err) {
        this.errorPresenter.showAsNonFatal(err);
      } finally {
        this.store.dispatch(decrementFullScreenSpinnerNecessity());
      }
    }
  };

  private transitionToYahooAdAssetLibrary = (yahooAdAccount: YahooAdAccount) => {
    const redirectURL = `https://ads.yahoo.co.jp/manager/#/${yahooAdAccount.adAccountId}/list/videos`; // @TODO FIX yahooの場合の連携後に確認するライブラリのpathを確認する
    window.open(`${redirectURL}`, '_blank');
  };

  private getCurrentOrganization(): OrganizationDetailFragment {
    const state: State = this.store.getState();

    let organization: OrganizationDetailFragment | null = null;
    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    if (!organization) {
      throw Error('organization was not found');
    }
    this.store.dispatch(setLastAccessedOrganization(organization));
    return organization;
  }
}

export default IntegrateYahooAdAccountUsecase;
